// src/features/players/components/GeoFencingSection.tsx

import { useState, useMemo } from 'react';
import {
  Stack, Group, Text, Select, MultiSelect, ActionIcon, TextInput,
} from '@mantine/core';
import { IconPlus } from '@tabler/icons-react';
import { CountryCodes } from '@lib2/model/form/CountryCodes';
import { StateUtil } from '@lib2/model/form/States';
import { DMACodes } from '@lib2/model/form/DMAS';
import {
  ACL_GEO_RULE_LABELS,
  GEO_FILTER_TYPES,
  GEO_FILTER_TYPE_LABELS,
  type AclGeoRuleTypeValue,
  type GeoFilterTypeValue,
} from '@/lib/constants/player';
import type { IAclRuleData } from '@/lib/types/Player';

interface GeoFencingSectionProps {
  ruleType: AclGeoRuleTypeValue;
  acls: IAclRuleData[];
  onAclsChange: (acls: IAclRuleData[]) => void;
}

/**
 * Reusable geo-fencing editor for a single ACL rule type.
 * Handles filter type selection (Any/Allow/Deny) and value management.
 *
 * Filter type is stored as independent local state (not derived from ACLs)
 * to fix the bug where switching from "Any" to "Allow/Deny" with 0 ACLs
 * would snap back to "Any" immediately. Parent form reset re-mounts this
 * component, so the useState initializer re-evaluates correctly.
 */
export function GeoFencingSection({ ruleType, acls, onAclsChange }: GeoFencingSectionProps) {
  const [inputValue, setInputValue] = useState('');
  const labels = ACL_GEO_RULE_LABELS[ruleType];

  // Independent filter type state — initialized from ACLs, not re-derived
  const [filterType, setFilterType] = useState<GeoFilterTypeValue>(() => {
    if (acls.length === 0) return 'any';
    return acls[0].isDeny ? 'deny' : 'allow';
  });

  // Build lookup data for the value selector
  const lookupData = useMemo(() => getLookupData(ruleType), [ruleType]);

  // Current selected values as string array (for MultiSelect)
  const selectedValues = useMemo(() => acls.map(a => a.value), [acls]);

  const handleFilterTypeChange = (newType: string | null) => {
    if (!newType) return;
    const typedNew = newType as GeoFilterTypeValue;
    setFilterType(typedNew);

    if (typedNew === 'any') {
      onAclsChange([]);
    } else if (acls.length > 0) {
      // Flip isDeny on existing ACLs
      const isDeny = typedNew === 'deny';
      onAclsChange(acls.map(acl => ({ ...acl, isDeny })));
    }
    // If 0 ACLs + allow/deny: just set local mode, user will add values next
  };

  // For MultiSelect: handle full value array changes
  const handleMultiSelectChange = (values: string[]) => {
    const isDeny = filterType === 'deny';
    const newAcls = values.map(value => ({
      aclRuleType: ruleType as IAclRuleData['aclRuleType'],
      isDeny,
      value,
    }));
    onAclsChange(newAcls);
  };

  // For postal codes: comma-separated text input
  const handleAddPostalCodes = (val: string | null) => {
    if (!val) return;
    const values = val.split(',').map(v => v.trim()).filter(Boolean);
    const isDeny = filterType === 'deny';
    const newAcls = [...acls];

    for (const value of values) {
      const exists = newAcls.some(a => a.value.toLowerCase() === value.toLowerCase());
      if (!exists) {
        newAcls.push({ aclRuleType: ruleType as IAclRuleData['aclRuleType'], isDeny, value });
      }
    }

    onAclsChange(newAcls);
    setInputValue('');
  };

  const handleRemovePostalCode = (value: string) => {
    onAclsChange(acls.filter(a => a.value.toLowerCase() !== value.toLowerCase()));
  };

  const isPostalCode = ruleType === 'geo_postalcode';

  return (
    <Stack gap="sm">
      <Text fw={500} size="sm">{labels.plural}</Text>

      <Select
        label="Filter Type"
        value={filterType}
        onChange={handleFilterTypeChange}
        data={GEO_FILTER_TYPES.map(t => ({ value: t, label: GEO_FILTER_TYPE_LABELS[t] }))}
        w={250}
      />

      {filterType !== 'any' && (
        <>
          {isPostalCode ? (
            <>
              <Group gap="xs" align="flex-end">
                <TextInput
                  label={`Add ${labels.plural}`}
                  placeholder="Enter zip codes (comma-separated)"
                  value={inputValue}
                  onChange={(e) => setInputValue(e.currentTarget.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleAddPostalCodes(inputValue);
                    }
                  }}
                  style={{ flex: 1 }}
                />
                <ActionIcon
                  variant="light"
                  size="lg"
                  onClick={() => handleAddPostalCodes(inputValue)}
                  disabled={!inputValue.trim()}
                >
                  <IconPlus size={16} />
                </ActionIcon>
              </Group>

              {acls.length > 0 && (
                <Group gap="xs" wrap="wrap">
                  {acls.map(acl => (
                    <Text
                      key={acl.value}
                      size="sm"
                      component="span"
                      style={{ cursor: 'pointer' }}
                      c="dimmed"
                      td="underline"
                      onClick={() => handleRemovePostalCode(acl.value)}
                      title="Click to remove"
                    >
                      {acl.value}
                    </Text>
                  ))}
                </Group>
              )}
            </>
          ) : (
            <MultiSelect
              label={`Select ${labels.plural}`}
              placeholder={`Search ${labels.plural.toLowerCase()}...`}
              data={lookupData}
              value={selectedValues}
              onChange={handleMultiSelectChange}
              searchable
              clearable
              nothingFoundMessage="No matches"
              maxDropdownHeight={300}
            />
          )}

          <Text size="xs" c="dimmed">
            {acls.length} {acls.length === 1 ? labels.singular.toLowerCase() : labels.plural.toLowerCase()}
            {' '}{filterType === 'allow' ? 'allowed' : 'denied'}
          </Text>
        </>
      )}
    </Stack>
  );
}

// --- Lookup data builders ---

/** Deduplicate by value and sort alphabetically by label */
function dedupeAndSort(items: Array<{ value: string; label: string }>): Array<{ value: string; label: string }> {
  const seen = new Set<string>();
  const unique: Array<{ value: string; label: string }> = [];
  for (const item of items) {
    if (!seen.has(item.value)) {
      seen.add(item.value);
      unique.push(item);
    }
  }
  return unique.sort((a, b) => a.label.localeCompare(b.label));
}

function getLookupData(ruleType: AclGeoRuleTypeValue): Array<{ value: string; label: string }> {
  switch (ruleType) {
    case 'geo_country':
      return dedupeAndSort(CountryCodes.ISO2Labels.map(([code, name]) => ({ value: code, label: `${name} (${code})` })));
    case 'geo_region':
      return dedupeAndSort(StateUtil.getStateCodeToLabelArray().map(([abbr, name]) => ({ value: abbr, label: `${name} (${abbr})` })));
    case 'geo_dma':
      return dedupeAndSort(DMACodes.DMACodeLabels.map(([code, name]) => ({ value: code, label: `${name} (${code})` })));
    case 'geo_postalcode':
      return []; // Postal codes use free-text input
  }
}
